import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Upload, CheckCircle2, AlertTriangle, X, FileSpreadsheet, Wand2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import {
  importCervezaMasterTemplate,
  autoFixCatalogFKs,
  type ImportResponse,
  type AutoFixResponse,
} from "@/lib/catalog-import.functions";


export const Route = createFileRoute("/admin/katalog-import")({
  component: KatalogImportPage,
});

type FileStatus = "pending" | "validating" | "validated" | "committing" | "committed" | "error";

type FileEntry = {
  id: string;
  file: File;
  status: FileStatus;
  validateResult?: ImportResponse;
  commitResult?: ImportResponse;
  error?: string;
  cachedFrom?: number;
};

function fileToBase64(file: File): Promise<string> {
  return file.arrayBuffer().then((buf) => {
    const bytes = new Uint8Array(buf);
    let bin = "";
    const chunk = 8192;
    for (let i = 0; i < bytes.length; i += chunk) {
      bin += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return btoa(bin);
  });
}

function summarize(r?: ImportResponse) {
  if (!r) return { rows: 0, upserted: 0, errors: 0, warnings: 0 };
  return {
    rows: r.validation?.total_rows ?? r.sheets.reduce((s, x) => s + x.rows_seen, 0),
    upserted: r.validation?.total_upserted ?? r.sheets.reduce((s, x) => s + x.rows_upserted, 0),
    errors: r.validation?.total_errors ?? r.sheets.reduce((s, x) => s + x.errors.length, 0),
    warnings: r.validation?.total_warnings ?? 0,
  };
}


const CACHE_KEY = "katalog-import:validate-cache:v1";

type CacheEntry = { key: string; result: ImportResponse; savedAt: number };

function fileKey(file: File) {
  return `${file.name}::${file.size}::${file.lastModified}`;
}

function loadCache(): Record<string, CacheEntry> {
  if (typeof window === "undefined") return {};
  try {
    return JSON.parse(window.localStorage.getItem(CACHE_KEY) ?? "{}");
  } catch {
    return {};
  }
}

function saveCache(cache: Record<string, CacheEntry>) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    /* quota */
  }
}

function upsertCache(file: File, result: ImportResponse) {
  const cache = loadCache();
  cache[fileKey(file)] = { key: fileKey(file), result, savedAt: Date.now() };
  saveCache(cache);
}

function removeFromCache(file: File) {
  const cache = loadCache();
  delete cache[fileKey(file)];
  saveCache(cache);
}

function KatalogImportPage() {
  const { hasRole, loading } = useAuth();
  const canAccess = hasRole("admin") || hasRole("catalog_editor");
  const [files, setFiles] = useState<FileEntry[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [running, setRunning] = useState<null | "validate" | "commit" | "auto-fix">(null);
  const [autoFix, setAutoFix] = useState<AutoFixResponse | null>(null);
  const [autoFixError, setAutoFixError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const runImport = useServerFn(importCervezaMasterTemplate);
  const runAutoFix = useServerFn(autoFixCatalogFKs);


  const addFiles = useCallback((incoming: FileList | File[]) => {
    const arr = Array.from(incoming).filter((f) =>
      f.name.toLowerCase().endsWith(".xlsx"),
    );
    const cache = loadCache();
    setFiles((prev) => [
      ...prev,
      ...arr.map((file) => {
        const key = fileKey(file);
        const cached = cache[key];
        return {
          id: `${key}-${Math.random().toString(36).slice(2, 8)}`,
          file,
          status: (cached ? "validated" : "pending") as FileStatus,
          validateResult: cached?.result,
          cachedFrom: cached?.savedAt,
        };
      }),
    ]);
  }, []);

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const removeFile = (id: string) =>
    setFiles((prev) => {
      const target = prev.find((f) => f.id === id);
      if (target) removeFromCache(target.file);
      return prev.filter((f) => f.id !== id);
    });
  const clearAll = () => {
    files.forEach((f) => removeFromCache(f.file));
    setFiles([]);
  };

  const updateEntry = (id: string, patch: Partial<FileEntry>) =>
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));

  const revalidate = async (id: string) => {
    const entry = files.find((f) => f.id === id);
    if (!entry) return;
    removeFromCache(entry.file);
    updateEntry(id, { status: "validating", error: undefined, cachedFrom: undefined, validateResult: undefined });
    try {
      const base64 = await fileToBase64(entry.file);
      const res = await runImport({ data: { fileName: entry.file.name, base64, mode: "validate" } });
      upsertCache(entry.file, res);
      updateEntry(id, { status: "validated", validateResult: res });
    } catch (e) {
      updateEntry(id, { status: "error", error: e instanceof Error ? e.message : String(e) });
    }
  };

  const runSequential = async (mode: "validate" | "commit") => {
    setRunning(mode);
    // Snapshot IDs at start so newly added files aren't picked up mid-run.
    const ids = files
      .filter((f) => {
        if (mode === "validate") {
          // Skip files already validated (cached or fresh) with no errors — user can re-validate manually.
          if (f.status === "validated") return false;
          return f.status !== "committed" && f.status !== "committing";
        }
        // commit only files that were successfully validated without errors
        return f.status === "validated" && summarize(f.validateResult).errors === 0;
      })
      .map((f) => f.id);

    for (const id of ids) {
      const entry = files.find((f) => f.id === id);
      if (!entry) continue;
      updateEntry(id, {
        status: mode === "validate" ? "validating" : "committing",
        error: undefined,
      });
      try {
        const base64 = await fileToBase64(entry.file);
        const res = await runImport({
          data: { fileName: entry.file.name, base64, mode },
        });
        if (mode === "validate") {
          upsertCache(entry.file, res);
          updateEntry(id, { status: "validated", validateResult: res, cachedFrom: undefined });
        } else {
          removeFromCache(entry.file);
          updateEntry(id, { status: "committed", commitResult: res });
        }
      } catch (e) {
        updateEntry(id, {
          status: "error",
          error: e instanceof Error ? e.message : String(e),
        });
      }
    }
    setRunning(null);
  };

  if (loading) return <div className="text-sm text-muted-foreground">Načítám…</div>;
  if (!canAccess) {
    return (
      <div className="max-w-md">
        <h1 className="font-serif text-2xl">Nemáte přístup</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Sekce je dostupná pouze administrátorům a uživatelům s rolí <em>catalog_editor</em>.
        </p>
      </div>
    );
  }

  const validatedOk = files.filter(
    (f) => f.status === "validated" && summarize(f.validateResult).errors === 0,
  ).length;
  const busy = running !== null;

  return (
    <div className="max-w-5xl space-y-6">
      <div>
        <h1 className="font-serif text-3xl">Import katalogu (Master Template 1.0)</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Přetáhněte jeden nebo více XLSX souborů odpovídajících standardu{" "}
          <strong>Cerveza International Data &amp; Import Standard 1.0</strong>. Nejprve
          spusťte ověření (dry-run) pro všechny soubory; po kontrole importujte. Import
          probíhá postupně, soubor po souboru.
        </p>
      </div>

      <Card
        className={`p-6 border-2 border-dashed transition-colors ${
          dragOver ? "border-primary bg-primary/5" : "border-muted-foreground/25"
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
      >
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <Upload className="h-10 w-10 text-muted-foreground" />
          <div className="text-sm">
            <strong>Přetáhněte XLSX soubory sem</strong> nebo{" "}
            <button
              type="button"
              className="text-primary underline"
              onClick={() => inputRef.current?.click()}
            >
              vyberte ze zařízení
            </button>
          </div>
          <div className="text-xs text-muted-foreground">
            Podporováno více souborů najednou. Každý bude ověřen a importován samostatně.
          </div>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.length) addFiles(e.target.files);
              e.target.value = "";
            }}
          />
        </div>

        {files.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              disabled={busy || files.every((f) => f.status === "validated" || f.status === "committed")}
              onClick={() => runSequential("validate")}
            >
              {running === "validate" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Ověřit neověřené (dry-run)
            </Button>
            <Button
              disabled={busy || validatedOk === 0}
              onClick={() => {
                if (
                  confirm(
                    `Opravdu importovat ${validatedOk} ověřený/ch soubor/ů do databáze?`,
                  )
                )
                  runSequential("commit");
              }}
            >
              {running === "commit" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Importovat ověřené ({validatedOk})
            </Button>
            <div className="flex-1" />
            <Button variant="ghost" size="sm" disabled={busy} onClick={clearAll}>
              Vymazat seznam
            </Button>
          </div>
        )}
      </Card>

      <Card className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <Wand2 className="mt-0.5 h-5 w-5 text-primary" />
          <div className="flex-1 space-y-1">
            <div className="font-medium">Auto-fix chybějících FK</div>
            <p className="text-xs text-muted-foreground">
              Deterministicky doplní běžné cizí klíče podle vazeb rodič → dítě a
              podle konvence identifikátorů standardu (např. město dědí zemi z
              regionu, pivovar zemi z primárního site, značka organizaci ze
              sdíleného <code>external_id</code> kmene <code>CEV-BRA-*</code> ↔{" "}
              <code>CEV-ORG-*</code>). Přepisují se pouze <em>NULL</em> hodnoty.
            </p>
          </div>
          <Button
            variant="outline"
            disabled={busy}
            onClick={async () => {
              if (!confirm("Spustit Auto-fix chybějících FK napříč katalogem?")) return;
              setRunning("auto-fix");
              setAutoFixError(null);
              try {
                const res = await runAutoFix();
                setAutoFix(res);
              } catch (e) {
                setAutoFixError(e instanceof Error ? e.message : String(e));
              } finally {
                setRunning(null);
              }
            }}
          >
            {running === "auto-fix" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Spustit Auto-fix
          </Button>
        </div>

        {autoFixError && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>{autoFixError}</div>
          </div>
        )}

        {autoFix && (
          <div className="rounded-md border bg-muted/10 p-3 text-xs space-y-2">
            <div className="flex items-center gap-2">
              <Badge variant={autoFix.total_fixed > 0 ? "default" : "secondary"}>
                Opraveno: {autoFix.total_fixed}
              </Badge>
              <span className="text-muted-foreground">Report běhu Auto-fix</span>
            </div>
            <ul className="grid gap-y-0.5">
              {autoFix.rules.map((r) => (
                <li key={r.rule} className="flex justify-between">
                  <code className="text-muted-foreground">{r.rule}</code>
                  <span className={r.fixed > 0 ? "text-emerald-700 font-medium" : "text-muted-foreground"}>
                    +{r.fixed}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card>



      {files.length > 0 && (
        <div className="space-y-3">
          {files.map((entry) => (
            <FileCard
              key={entry.id}
              entry={entry}
              onRemove={() => removeFile(entry.id)}
              onRevalidate={() => revalidate(entry.id)}
              busy={busy}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: FileStatus }) {
  const map: Record<FileStatus, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
    pending: { label: "Čeká", variant: "outline" },
    validating: { label: "Ověřuji…", variant: "secondary" },
    validated: { label: "Ověřeno", variant: "secondary" },
    committing: { label: "Importuji…", variant: "secondary" },
    committed: { label: "Importováno", variant: "default" },
    error: { label: "Chyba", variant: "destructive" },
  };
  const cfg = map[status];
  return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
}

function FileCard({
  entry,
  onRemove,
  onRevalidate,
  busy,
}: {
  entry: FileEntry;
  onRemove: () => void;
  onRevalidate: () => void;
  busy: boolean;
}) {
  const result = entry.commitResult ?? entry.validateResult;
  const totals = summarize(result);
  const isBusy = entry.status === "validating" || entry.status === "committing";
  const isCached = entry.cachedFrom != null && entry.status === "validated";

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-center gap-3">
        <FileSpreadsheet className="h-5 w-5 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">{entry.file.name}</div>
          <div className="text-xs text-muted-foreground">
            {(entry.file.size / 1024).toFixed(1)} KB
            {isCached && (
              <> · <span title={new Date(entry.cachedFrom!).toLocaleString()}>načteno z paměti</span></>
            )}
          </div>
        </div>
        {isCached && <Badge variant="outline">z cache</Badge>}
        <StatusBadge status={entry.status} />
        {entry.status === "validated" && (
          <Button
            variant="ghost"
            size="sm"
            disabled={busy || isBusy}
            onClick={onRevalidate}
          >
            Znovu ověřit
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          disabled={busy || isBusy}
          onClick={onRemove}
          aria-label="Odebrat"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {entry.error && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>{entry.error}</div>
        </div>
      )}

      {result?.fatal_error && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>{result.fatal_error}</div>
        </div>
      )}

      {result && !result.fatal_error && (
        <>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <Badge variant={result.mode === "commit" ? "default" : "secondary"}>
              {result.mode === "commit" ? "Import proveden" : "Ověření (dry-run)"}
            </Badge>
            <span>Standard: <strong>{result.standard_version ?? "?"}</strong></span>
            <span>· Řádků: <strong>{totals.rows}</strong></span>
            <span>· Uloženo: <strong>{totals.upserted}</strong></span>
            <span className={totals.errors > 0 ? "text-destructive" : "text-muted-foreground"}>
              · Chyb: <strong>{totals.errors}</strong>
            </span>
            <span className={totals.warnings > 0 ? "text-amber-600" : "text-muted-foreground"}>
              · Varování: <strong>{totals.warnings}</strong>
            </span>
            {result.import_log_id && (
              <span className="text-muted-foreground">
                (log {result.import_log_id.slice(0, 8)}…)
              </span>
            )}
          </div>

          {result.validation && (
            <ValidationReport validation={result.validation} />
          )}

          <div className="grid gap-1.5">
            {result.sheets.map((s) => (
              <div
                key={s.sheet}
                className="flex flex-wrap items-center gap-2 rounded border bg-muted/20 px-2 py-1.5 text-xs"
              >
                {s.errors.length === 0 && s.warnings.length === 0 ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                ) : s.errors.length > 0 ? (
                  <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
                ) : (
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                )}
                <strong>{s.sheet}</strong>
                <span className="text-muted-foreground">→ {s.target_table}</span>
                <span className="ml-auto">
                  řádků: {s.rows_seen} · uloženo: {s.rows_upserted} · přeskočeno:{" "}
                  {s.rows_skipped}
                  {s.errors.length > 0 && ` · chyb: ${s.errors.length}`}
                  {s.warnings.length > 0 && ` · varování: ${s.warnings.length}`}
                </span>
              </div>
            ))}
            {result.sheets.some((s) => s.errors.length > 0 || s.warnings.length > 0) && (
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground">
                  Zobrazit chyby a varování
                </summary>
                <ul className="mt-2 max-h-60 space-y-1 overflow-auto">
                  {result.sheets.flatMap((s) =>
                    s.errors.slice(0, 50).map((e, i) => (
                      <li key={`err-${s.sheet}-${i}`} className="text-destructive">
                        <strong>{s.sheet}</strong> ř. {e.row}: {e.message}
                      </li>
                    )),
                  )}
                  {result.sheets.flatMap((s) =>
                    s.warnings.slice(0, 50).map((w, i) => (
                      <li key={`warn-${s.sheet}-${i}`} className="text-amber-700">
                        <strong>{s.sheet}</strong> ř. {w.row}
                        {w.field ? ` [${w.field}]` : ""}: {w.message}
                      </li>
                    )),
                  )}
                </ul>
              </details>
            )}
          </div>
        </>
      )}
    </Card>
  );
}

function ValidationReport({
  validation,
}: {
  validation: NonNullable<ImportResponse["validation"]>;
}) {
  const fkEntries = Object.entries(validation.missing_fk_totals).sort(
    (a, b) => b[1] - a[1],
  );
  const badgeVariant =
    validation.severity === "errors"
      ? "destructive"
      : validation.severity === "warnings"
        ? "secondary"
        : "default";
  const label =
    validation.severity === "errors"
      ? "Chyby v datech"
      : validation.severity === "warnings"
        ? "Varování"
        : "Bez problémů";

  if (
    validation.severity === "ok" &&
    fkEntries.length === 0 &&
    validation.sheets_missing.length === 0
  ) {
    return null;
  }

  return (
    <div className="rounded-md border bg-muted/10 p-3 text-xs space-y-2">
      <div className="flex items-center gap-2">
        <Badge variant={badgeVariant}>{label}</Badge>
        <span className="text-muted-foreground">
          Validační report této dávky
        </span>
      </div>
      {validation.sheets_missing.length > 0 && (
        <div>
          <strong>Chybějící listy:</strong>{" "}
          <span className="text-destructive">
            {validation.sheets_missing.join(", ")}
          </span>
        </div>
      )}
      {validation.orphan_parent_rows > 0 && (
        <div>
          <strong>Chybějící parent relace</strong> (admin_areas bez parenta):{" "}
          <span className="text-amber-700">{validation.orphan_parent_rows}</span>
        </div>
      )}
      {fkEntries.length > 0 && (
        <div>
          <strong>Chybějící FK dle sloupce:</strong>
          <ul className="mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5">
            {fkEntries.map(([fk, n]) => (
              <li key={fk} className="flex justify-between">
                <code className="text-muted-foreground">{fk}</code>
                <span className="text-amber-700">{n}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

